import { ViewState } from '@cibo/mapgl'
import { useUserWebSocket } from '@cibo/profile'
import { US_CENTER } from '@cibo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState } from 'react'
import { QUERY_KEY } from '../../queries/queryKey'
import { useDeleteFarm } from '../../queries/useFarms'
import { LandManagerRoutes } from '../../types'
import { AddLabelsModal } from '../AddLabelsModal'
import { AssignUserEditor } from '../Editors/AssignUserEditor'
import { LandDeleteForm } from '../LandDeleteForm'
import {
  UseAssignFarmDialog,
  UseFarmDialog,
  UseFieldMetaDialog,
  UseImportFieldAlyticsDialog,
  useAssignFarmDialog,
  useFarmDialog,
  useFieldMetaDialog,
  useImportFieldAlyticsDialog,
} from './hooks'

type LandManagementModalState = {
  open: boolean
  selectedIds?: string[]
  onSuccess?: (value?: any) => void
}
export type LandManagementModalStateSingleField = {
  open: boolean
  fieldId?: string
  onSuccess?: (value?: any) => void
}

export interface LandManagementState
  extends Pick<UseFieldMetaDialog, 'openFieldMetaDialog'>,
    Pick<UseFarmDialog, 'openFarmDialog'>,
    Pick<UseAssignFarmDialog, 'openAssignFarmDialog'>,
    Pick<UseImportFieldAlyticsDialog, 'openImportFieldAlyticsDialog'> {
  setAssignFieldsModal(state: LandManagementModalState): void
  setDeleteModal(state: LandManagementModalState): void
  setLabelModal(state: LandManagementModalState): void

  confirmDeleteField(resourceId: string): Promise<any>
  confirmDeleteFarm(resourceId: string): Promise<any>

  LAND_MANAGER_ROUTES: LandManagerRoutes

  editMapViewState: ViewState
  setEditMapViewState(newViewState: ViewState): void
}

export const LandManagementContext = createContext<LandManagementState>({
  setAssignFieldsModal: console.log,
  setDeleteModal: console.log,
  setLabelModal: console.log,

  confirmDeleteField: (resourceId: string) => new Promise(resolve => {}),
  confirmDeleteFarm: (resourceId: string) => new Promise(resolve => {}),

  LAND_MANAGER_ROUTES: {
    DETAIL: '',
    EDIT: '',
    UPLOAD: '',
    DRAW: '',
    ROOT: '',
    PROFILE_FIELDS: '',
  },

  openFieldMetaDialog: console.log,
  openFarmDialog: console.log,
  openAssignFarmDialog: console.log,
  openImportFieldAlyticsDialog: console.log,

  editMapViewState: {
    longitude: US_CENTER[0],
    latitude: US_CENTER[1],
    zoom: 4,
  },
  setEditMapViewState: console.log,
})

export const LandManagementProvider: React.FC<{ LAND_MANAGER_ROUTES: LandManagerRoutes }> = ({
  LAND_MANAGER_ROUTES,
  children,
}) => {
  const queryClient = useQueryClient()
  const { lastMessage } = useUserWebSocket('autoCalc')

  const [assignFieldsModal, setAssignFieldsModal] = useState<LandManagementModalState>({
    open: false,
  })
  const [deleteModal, setDeleteModal] = useState<LandManagementModalState>({ open: false })
  const [labelModal, setLabelModal] = useState<LandManagementModalState>({ open: false })

  const [editMapViewState, setEditMapViewState] = useState({
    longitude: US_CENTER[0],
    latitude: US_CENTER[1],
    zoom: 4,
  })

  const deleteFarm = useDeleteFarm()

  const { fieldMetaDialog, openFieldMetaDialog } = useFieldMetaDialog()
  const { farmDialog, openFarmDialog } = useFarmDialog()
  const { assignFarmDialog, openAssignFarmDialog } = useAssignFarmDialog()
  const { importFieldAlyticsDialog, openImportFieldAlyticsDialog } = useImportFieldAlyticsDialog()

  useEffect(() => {
    if (lastMessage?.subject === 'autoCalc') {
      const resourceIds = lastMessage.entityIds as string[]

      resourceIds.forEach(resourceId =>
        queryClient.invalidateQueries([QUERY_KEY.FIELDS, resourceId])
      )
      queryClient.invalidateQueries([QUERY_KEY.FIELDS_PAGINATED])
    }
  }, [lastMessage, queryClient])

  return (
    <LandManagementContext.Provider
      value={{
        setAssignFieldsModal,
        setDeleteModal,
        setLabelModal,

        openFieldMetaDialog,
        openFarmDialog,
        openAssignFarmDialog,
        openImportFieldAlyticsDialog,

        confirmDeleteField: (resourceId: string) =>
          new Promise(resolve => {
            setDeleteModal({ open: true, selectedIds: [resourceId], onSuccess: resolve })
          }),

        // @TODO: provide a modal to confirm and optionally cascade deletes to fields(!?)
        confirmDeleteFarm: (resourceId: string) => deleteFarm.mutateAsync(resourceId),

        LAND_MANAGER_ROUTES,

        editMapViewState,
        setEditMapViewState,
      }}
    >
      {children}

      <LandDeleteForm
        selectedIds={deleteModal.selectedIds}
        visible={deleteModal.open}
        onRequestClose={() => setDeleteModal({ open: false })}
        onSuccess={() => {
          deleteModal.onSuccess && deleteModal.onSuccess()
        }}
      />

      <AddLabelsModal
        target={labelModal.selectedIds}
        visible={labelModal.open}
        onRequestClose={() => {
          // @todo set page zero from consumer
          // setPage(0)
          setLabelModal({ open: false })
        }}
      />

      <AssignUserEditor
        open={assignFieldsModal.open}
        onCancel={() => setAssignFieldsModal({ open: false })}
        onSuccess={() => setAssignFieldsModal({ open: false })}
        resourceIds={assignFieldsModal.selectedIds || []}
      />

      {fieldMetaDialog}
      {farmDialog}
      {assignFarmDialog}
      {importFieldAlyticsDialog}
    </LandManagementContext.Provider>
  )
}

export const useLandManagement = () => useContext(LandManagementContext)
